<template>
  <div v-show="billboards.length > 0" class="billboard-carousel-container">
    <Carousel
      v-if="billboards.length > 0"
      alt-prev-text="Previous"
      alt-next-text="Next"
      class="billboard-carousel"
      lazy-load="progressive"
      :class="{ 'with-tnc': hasTncBillboards.length }"
      :autoplay="true"
      :autoplay-speed="7000"
      :dots="true"
      :infinite="true"
      :arrows="!$device.isMobile"
      :overlay-arrows="true"
      :pause-on-dots-hover="true"
      :pause-on-hover="true"
      :speed="1000"
      :swipe="false"
      :responsive="responsive"
      data-analytics-id="homepage-container"
      data-analytics-label="Billboard"
      :adaptive-height="false"
      @afterChange="$event => setCarouselIndex($event)"
    >
      <!-- Setting a default value for href (:to) in order to avoid LH error on CI -->
      <!-- Ref: https://github.com/sephora-asia/web-ui/pull/973/checks?check_run_id=3296716782 -->
      <!-- TODO: Add validation for this attribute in the billboard model in luxola  -->
      <div class="billboard" v-for="(billboard, index) in billboards" :key="billboard.id">
        <SmartLink
          :native="false"
          :to="billboard.url || '#'"
          :target="billboard.target"
          :rel="billboard.rel"
          :aria-label="billboard.url"
          data-analytics-id="homepage-link"
          data-analytics-action="main-billboard"
          :data-analytics-label="billboard.url"
          :data-analytics-record-id="billboard.id"
          @click="trackBillboardClicked(index)"
        >
          <div
            class="billboard-image-container"
            :class="{ mobile: $device.isMobile }"
          >
            <!-- Setting a default value for image alt text in order to avoid LH error on CI -->
            <!-- Ref: https://github.com/sephora-asia/web-ui/pull/973/checks?check_run_id=3296716782 -->
            <!-- TODO: Add validation for this attribute in the billboard_image model in luxola  -->
            <nuxt-img
              :src="billboard.imgSrc"
              :alt="billboard.altText || `Billboard ${billboard.id}`"
              class="billboard-image"
              sizes="xs:775px sm:930px md:930px lg:1125px"
            />
          </div>
        </SmartLink>
        <div class="tnc-link-container">
          <template v-if="billboard.termsAndConditions">
            <a
              href="#"
              class="tnc-link ga-hp-link ga-hp-link-main-billboard"
              @click.prevent="
                showTermsAndConditions(billboard.termsAndConditions)
              "
            >
              {{ $t('billboard.termsAndConditions') }}
            </a>
          </template>
        </div>
      </div>
    </Carousel>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Carousel from '@sephora-asia/core-carousel/Carousel.vue'
import impressionTracking from '@sephora-asia/common-ui/mixins/impressionTracking.js'

export default {
  name: 'BillboardCarouselContainer',
  components: {
    Carousel
  },
  mixins: [impressionTracking],
  data() {
    return {
      currentCarouselIndex: 0
    }
  },
  computed: {
    ...mapGetters({
      billboards: 'billboard/billboards'
    }),
    hasTncBillboards() {
      return this.billboards.filter((billboard) => billboard.termsAndConditions)
    },
    responsive() {
      return [
        {
          breakpoint: 1023,
          settings: {
            swipe: true
          }
        }
      ]
    }
  },
  methods: {
    showTermsAndConditions(termsAndConditions) {
      this.$emit('show-modal', termsAndConditions)
    },
    setCarouselIndex(carouselIndex) {
      this.currentCarouselIndex = carouselIndex

      if (this.inViewport.now) {
        this.trackImpression()
      }
    },
    trackImpression() {
      if (this.billboards.length === 0) {
        return
      }

      // setting adaptiveHeight to false due to vue-slick-carousel issue 
      // https://github.com/gs-shop/vue-slick-carousel/issues/115
      const params = {
        billboard_position: this.currentCarouselIndex + 1,
        billboard_id: this.billboards[this.currentCarouselIndex]?.id
      }

      this.$analytics.trackEvent('homepage_impression_billboard', { params })
    },
    trackBillboardClicked(billboardIndex) {
      if (this.billboards.length === 0) {
        return
      }

      const params = {
        billboard_position: billboardIndex + 1,
        billboard_id: this.billboards[billboardIndex]?.id
      }

      this.$analytics.trackEvent('homepage_click_billboard', { params })
    }
  }
}
</script>

<style lang="scss" scoped>
.billboard-carousel-container {
  margin-bottom: 16px;
  width: 100%;

  &.with-tnc {
    .tnc-link-container {
      min-height: 20px;
    }
  }
}

.billboard-image-container {
  position: relative;
  padding-top: 44.44%;

  &.mobile {
    padding-top: 75.08%;
  }
}
.billboard-image {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
}

.tnc-link-container {
  display: block;
  text-align: left;

  .tnc-link {
    display: inline-block;
    padding: 8px 10px;
    text-decoration: underline;
    font-size: 11px;
    background: $white;
    color: $grey-darker;
    transition: all 0.3s ease 0s;

    &:hover {
      background: $grey-dark;
      color: $white;
    }
  }
}


</style>
